import { useState, useEffect, useRef } from 'react'
import { IOfferScopeFilters, IFilterConfig } from '../types'
import { constructNewFilterFromConfig, constructUserFiltersFromUrl } from '../components/filters'
import * as lib from '../lib'
import { EFilterOperator, EFilterType, EFilterSpecOperator, EFilterFetchConvertType } from '../types/enum'
import { useAtomValue } from 'jotai'
import * as state from '../states'
import { OFFER_SCOPE_CAP_TYPE_OPTIONS, OFFER_SCOPE_STATUS_OPTIONS } from '../constants'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'

type IUseFiltersOfferScopePage = {
	filter: [string, any][];
	send: boolean;
	activeFiltersCount: number;
	filterFillFactor: number;
	isFiltersPanelOpen: boolean;
	setIsFiltersPanelOpen: (value: (((prevState: boolean) => boolean) | boolean)) => void;
	offerScopeFiltersConfig: IFilterConfig<keyof IOfferScopeFilters>[];
	defaultFiltersValue: IOfferScopeFilters;
	initialFiltersValue: IOfferScopeFilters;
	clearChildFilters: number;
	setUserFilters: (value: (((prevState: (IOfferScopeFilters | null)) => (IOfferScopeFilters | null)) | IOfferScopeFilters | null)) => void;
}

const OFFER_SCOPE_FILTERS_DEFAULT: IOfferScopeFilters = {
	name: '',
	'stats.ids': null,
	capType: null,
	status: null,
	affiliateNetwork: null,
	country: null
}


export const useFiltersOfferScopePage = (group: string): IUseFiltersOfferScopePage => {
	const { t } = useTranslation()
	const isFirstRender = useRef<boolean>(true)
	const [searchParams, setSearchParams] = useSearchParams()
	const [groupPrev, setGroupPrev] = useState<string>(group)

	const profile = useAtomValue(state.profileAtom)
	//TODO: REMOVE CRUTCH - ACCESS FOR TEAM-X-USERS'
	const isTeam = useAtomValue(state.isTeamAtom)
	const profileRole = profile?.role


	const urlFilter = (() => {
		try {
			return lib.filterSearchParams(searchParams.get('f')) ?? []
		} catch (error) {
			console.error(error)
			return []
		}
	})()

	const [filter, setFilter] = useState<[string, any][]>([...urlFilter]) // main filter with mixins
	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState<boolean>(false)
	const [activeFiltersCount, setActiveFiltersCount] = useState<number>(0)

	const [clearChildFilters, setClearChildFilters] = useState<number>(0)
	const [send, setSend] = useState<boolean>(false)
	const [userFilters, setUserFilters] = useState<IOfferScopeFilters | null>(null)

	const filterFillFactor = !!userFilters ? (100 * activeFiltersCount / Object.keys(userFilters).length) : 0

	const countryOptions = useAtomValue(state.countryOptionsAtom)
	const affiliateNetworkOptions = useAtomValue(state.affiliateNetworkOptionsAtom)
	const OFFER_SCOPE_CAP_TYPE_OPTIONS_TRANS = OFFER_SCOPE_CAP_TYPE_OPTIONS(t)
	const OFFER_SCOPE_STATUS_OPTIONS_TRANS = OFFER_SCOPE_STATUS_OPTIONS(t)

	const offerScopeFiltersConfig: IFilterConfig<keyof IOfferScopeFilters>[] = [
		{
			name: 'name',
			label: t('offer_scope_list_table.column_name'),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'stats.ids',
			label: t('offer_scope_list_table.column_offer_id'),
			type: EFilterType.input,
			fetchConvertType: EFilterFetchConvertType.numberArray,
			operator: EFilterOperator.isSubSet,
			styles: { width: '150px' }
		},
		{
			name: 'affiliateNetwork',
			label: t('offer_scope_list_table.column_affiliate_network'),
			type: EFilterType.selectMultiple,
			operator: EFilterOperator.isSubSet,
			options: affiliateNetworkOptions ?? [],
			styles: { minWidth: '200px' }
		},
		{
			name: 'country',
			label: t('offer_scope_list_table.column_country'),
			type: EFilterType.selectMultiple,
			operator: EFilterOperator.isSubSet,
			options: countryOptions ?? [],
			styles: { minWidth: '200px' }
		},
		{
			name: 'capType',
			label: t('offer_scope_list_table.column_cap_type'),
			type: EFilterType.selectSingle,
			operator: EFilterOperator.isEqual,
			options: OFFER_SCOPE_CAP_TYPE_OPTIONS_TRANS,
			styles: { minWidth: '200px' }
		},
		{
			name: 'status',
			label: t('offer_scope_list_table.column_status'),
			type: EFilterType.selectSingle,
			operator: EFilterOperator.isEqual,
			options: OFFER_SCOPE_STATUS_OPTIONS_TRANS,
			styles: { minWidth: '160px' }
		}
	]

	const initialUserFilters = constructUserFiltersFromUrl(
		urlFilter,
		offerScopeFiltersConfig,
		OFFER_SCOPE_FILTERS_DEFAULT
	)

	useEffect(() => {
		let _userFilters = userFilters
		//console.log('hook.useFiltersOfferScopePage useEffect::START:: ', { group, groupPrev, userFilters, _userFilters, filter })
		if (send && groupPrev !== group) {
			// reset filters [RFC] onGroup (menu tabs) changed
			setGroupPrev(group)
			//console.log('!!! clear main filter:: groupPrev !== group !!!')
			setFilter([]) // clear main filter with mixins
			_userFilters = OFFER_SCOPE_FILTERS_DEFAULT
			setUserFilters(OFFER_SCOPE_FILTERS_DEFAULT)
			setClearChildFilters(prev => prev + 1) // clearAllChildFilters command-flag
		}

		if (!!_userFilters) {
			setSend(true)
			//console.log('hook.useFiltersOfferScopePage useEffect::MIDDLE:: ', { userFilters, _userFilters, filter })
			const { newFilter, activeFiltersCnt } = constructNewFilterFromConfig(
				_userFilters,
				offerScopeFiltersConfig
			)
			const newFilterStr = lib.filterSetup([...newFilter ?? []])

			setSearchParams((prev) => {
				if (newFilterStr) {
					prev.set('f', newFilterStr)
				} else {
					prev.delete('f')
				}

				// after first pageLoad/pageRefresh resetting is not needed
				if (!isFirstRender.current) {
					prev.delete('page')
				} else {
					isFirstRender.current = false
				}

				if (!!isTeam.length && !isTeam.includes(prev.get('group') ?? '')) {
					prev.set('group', encodeURIComponent(group))
				}

				return prev
			})

			if (group) {
				newFilter.push([`group${EFilterOperator.isEqual}`, group])
			}

			setFilter(newFilter)
			setActiveFiltersCount(activeFiltersCnt)

			// to open FiltersPanel if there were previously active filters on the page [onRefreshPage case]
			if (activeFiltersCnt > 0) {
				setIsFiltersPanelOpen(true)
			}
			//console.log('hook.useFiltersOfferScopePage:: useEffect::END:: ', { userFilters, _userFilters, filter, newFilter, newFilterStr })
		}
	}, [userFilters, group])

	return {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		offerScopeFiltersConfig,
		defaultFiltersValue: OFFER_SCOPE_FILTERS_DEFAULT,
		initialFiltersValue: initialUserFilters,
		clearChildFilters,
		setUserFilters
	}
}